import { useState, useEffect, useMemo } from 'react';
import { api } from '../../../services/api';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
} from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import { PawPrint, Users, MapPin, Package, Trophy } from 'lucide-react';

// Registrar componentes de Chart.js
ChartJS.register(
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement,
    Title
);

const COLORS = ['#4E3F7F', '#d16fa3', '#16a34a', '#d97706', '#0ea5e9', '#8b5cf6'];

const extractData = (res) => {
    const payload = res.data;
    return Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload) ? payload : []);
};

const ChartCard = ({ title, icon: Icon, iconColor, children, span }) => (
    <div className="sp-card" style={{ gridColumn: span ? '1 / -1' : undefined }}>
        <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <Icon size={18} color={iconColor} /> {title}
        </h3>
        {children}
    </div>
);

const EstadisticasPanel = () => {
    const [data, setData] = useState({ mascotas: [], usuarios: [], ubicaciones: [], productos: [] });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchAll = async () => {
            try {
                const [m, u, ub, p] = await Promise.all([
                    api.getMascotas().catch(() => ({ data: { data: [] } })),
                    api.getUsuarios().catch(() => ({ data: { data: [] } })),
                    api.getUbicaciones().catch(() => ({ data: { data: [] } })),
                    api.getProductos().catch(() => ({ data: { data: [] } })),
                ]);
                setData({
                    mascotas: extractData(m),
                    usuarios: extractData(u),
                    ubicaciones: extractData(ub),
                    productos: extractData(p),
                });
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, []);

    // Datos para gráficos
    const sexoData = useMemo(() => {
        const machos = data.mascotas.filter(m => m.sexo == 0).length;
        const hembras = data.mascotas.filter(m => m.sexo == 1).length;
        const sinDato = data.mascotas.length - machos - hembras;
        return {
            labels: ['Machos', 'Hembras', 'Sin dato'],
            datasets: [{
                data: [machos, hembras, sinDato],
                backgroundColor: [COLORS[0], COLORS[1], COLORS[2]],
                borderWidth: 1,
            }],
        };
    }, [data.mascotas]);

    const escaneosPorDia = useMemo(() => {
        const map = {};
        data.ubicaciones.forEach(u => {
            const fecha = (u.fecha_hora || '').slice(0, 10);
            if (!fecha) return;
            map[fecha] = (map[fecha] || 0) + 1;
        });
        const sorted = Object.entries(map)
            .sort((a, b) => a[0].localeCompare(b[0]))
            .slice(-7);
        return {
            labels: sorted.map(([fecha]) => fecha.slice(5)),
            datasets: [{
                label: 'Escaneos',
                data: sorted.map(([, total]) => total),
                backgroundColor: '#4E3F7F',
                borderRadius: 6,
            }],
        };
    }, [data.ubicaciones]);

    const usuariosNotif = useMemo(() => {
        const si = data.usuarios.filter(u => u.recibir_emails == 1).length;
        const no = data.usuarios.length - si;
        return {
            labels: ['Reciben emails', 'No reciben'],
            datasets: [{
                data: [si, no],
                backgroundColor: [COLORS[4], COLORS[5]],
                borderWidth: 1,
            }],
        };
    }, [data.usuarios]);

    const productosStock = useMemo(() => {
        const activos = data.productos.filter(p => p.activo == 1).length;
        const inactivos = data.productos.length - activos;
        const oferta = data.productos.filter(p => p.oferta == 1).length;
        return {
            labels: ['Activos', 'Inactivos', 'En oferta'],
            datasets: [{
                label: 'Cantidad',
                data: [activos, inactivos, oferta],
                backgroundColor: ['#16a34a', '#d97706', '#ec4899'],
                borderRadius: 6,
            }],
        };
    }, [data.productos]);

    const rankingMascotas = useMemo(() => {
        const conteo = {};
        data.ubicaciones.forEach(u => {
            conteo[u.id_mascota] = (conteo[u.id_mascota] || 0) + 1;
        });
        return Object.entries(conteo)
            .map(([id_mascota, escaneos]) => ({
                id_mascota,
                escaneos,
                nombre: data.mascotas.find(m => String(m.id) === String(id_mascota))?.nombre || `Mascota #${id_mascota}`,
            }))
            .sort((a, b) => b.escaneos - a.escaneos)
            .slice(0, 5);
    }, [data.ubicaciones, data.mascotas]);

    if (loading) return <div className="sp-card"><p>Cargando estadísticas...</p></div>;

    return (
        <div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '1.5rem' }}>Estadísticas</h1>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.25rem' }}>

                <ChartCard title="Mascotas por sexo" icon={PawPrint} iconColor="#d16fa3">
                    <div style={{ height: 220 }}>
                        <Pie data={sexoData} options={{ maintainAspectRatio: false }} />
                    </div>
                </ChartCard>

                <ChartCard title="Escaneos (últimos 7 días)" icon={MapPin} iconColor="#16a34a">
                    <div style={{ height: 220 }}>
                        <Bar
                            data={escaneosPorDia}
                            options={{
                                maintainAspectRatio: false,
                                scales: {
                                    y: { beginAtZero: true, precision: 0 },
                                },
                            }}
                        />
                    </div>
                </ChartCard>

                <ChartCard title="Preferencia de notificaciones" icon={Users} iconColor="#0ea5e9">
                    <div style={{ height: 220 }}>
                        <Pie data={usuariosNotif} options={{ maintainAspectRatio: false }} />
                    </div>
                </ChartCard>

                <ChartCard title="Estado del catálogo" icon={Package} iconColor="#ec4899">
                    <div style={{ height: 220 }}>
                        <Bar
                            data={productosStock}
                            options={{
                                maintainAspectRatio: false,
                                scales: {
                                    y: { beginAtZero: true, precision: 0 },
                                },
                            }}
                        />
                    </div>
                </ChartCard>

                <ChartCard title="Mascotas más escaneadas" icon={Trophy} iconColor="#d97706" span>
                    {rankingMascotas.length === 0 ? (
                        <p style={{ color: 'var(--sp-muted)' }}>Sin escaneos registrados aún.</p>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                            {rankingMascotas.map((r, i) => (
                                <div key={r.id_mascota} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                    <span style={{ fontWeight: 800, width: 26, color: 'var(--sp-primary)' }}>#{i + 1}</span>
                                    <span style={{ flex: 1, fontWeight: 600 }}>{r.nombre}</span>
                                    <span style={{ fontSize: '0.85rem', color: 'var(--sp-muted)' }}>
                                        {r.escaneos} escaneo{r.escaneos !== 1 ? 's' : ''}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </ChartCard>

            </div>
        </div>
    );
};

export default EstadisticasPanel;